/**
 * Tickets API Module
 * All ticket-related API endpoints
 */

import { api } from './client.js';

/**
 * Tickets API endpoints
 */
export const ticketsApi = {
  /**
   * Get all tickets with optional filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10, max: 100)
   * @param {string} params.status - Filter by status (open, in_progress, resolved)
   * @param {string} params.priority - Filter by priority (low, medium, high)
   * @param {string} params.search - Search term for subject, company, ticket_id, or description
   * @returns {Promise<Object>} Tickets data with pagination
   */
  getAll: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/tickets${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Get ticket by ID
   * @param {string|number} id - Ticket ID
   * @returns {Promise<Object>} Ticket data with messages
   */
  getById: (id) => {
    if (!id) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    return api.get(`/admin/tickets/${id}`);
  },

  /**
   * Get ticket by Ticket ID (TKT-XXXX)
   * @param {string} ticketId - Ticket ID (e.g., TKT-1009)
   * @returns {Promise<Object>} Ticket data with messages
   */
  getByTicketId: (ticketId) => {
    if (!ticketId) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    return api.get(`/admin/tickets/ticket/${ticketId}`);
  },

  /**
   * Create new ticket
   * @param {Object} ticketData - Ticket data
   * @param {string} ticketData.subject - Subject (required)
   * @param {number} [ticketData.companyId] - Company ID (preferred)
   * @param {string} [ticketData.company] - Company name (legacy fallback)
   * @param {string} [ticketData.description] - Description
   * @param {string} [ticketData.status] - Status (open, in_progress, resolved)
   * @param {string} [ticketData.priority] - Priority (low, medium, high)
   * @returns {Promise<Object>} Created ticket data
   */
  create: (ticketData) => {
    if (!ticketData || !ticketData.subject || (!ticketData.companyId && !ticketData.company)) {
      return Promise.reject(new Error('Subject and companyId/company are required'));
    }
    return api.post('/admin/tickets', ticketData);
  },

  /**
   * Update ticket
   * @param {string|number} id - Ticket ID
   * @param {Object} ticketData - Updated ticket data (all fields optional)
   * @returns {Promise<Object>} Updated ticket data
   */
  update: (id, ticketData) => {
    if (!id) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    if (!ticketData || Object.keys(ticketData).length === 0) {
      return Promise.reject(new Error('Ticket data is required'));
    }
    return api.put(`/admin/tickets/${id}`, ticketData);
  },

  /**
   * Delete ticket (soft delete)
   * @param {string|number} id - Ticket ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: (id) => {
    if (!id) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    return api.delete(`/admin/tickets/${id}`);
  },

  /**
   * Update ticket status
   * @param {string|number} id - Ticket ID
   * @param {string} status - New status (open, in_progress, resolved)
   * @param {string} [note] - Optional note explaining the status change
   * @returns {Promise<Object>} Updated ticket data
   */
  updateStatus: (id, status, note) => {
    if (!id) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    if (!status) {
      return Promise.reject(new Error('Status is required'));
    }
    const body = { status };
    const trimmedNote = String(note || '').trim();
    if (trimmedNote) {
      body.note = trimmedNote;
    }
    return api.patch(`/admin/tickets/${id}/status`, body);
  },

  /**
   * Add message to ticket
   * @param {string|number} ticketId - Ticket ID
   * @param {Object} messageData - Message data
   * @param {string} messageData.message - Message content (required)
   * @param {string} [messageData.senderName] - Sender name
   * @param {string} [messageData.senderRole] - Sender role (admin, customer, company)
   * @returns {Promise<Object>} Created message and updated ticket
   */
  addMessage: (ticketId, messageData) => {
    if (!ticketId) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    if (!messageData || !messageData.message) {
      return Promise.reject(new Error('Message is required'));
    }
    return api.post(`/admin/tickets/${ticketId}/messages`, messageData);
  },

  /**
   * Get ticket messages
   * @param {string|number} ticketId - Ticket ID
   * @returns {Promise<Object>} Messages array
   */
  getMessages: (ticketId) => {
    if (!ticketId) {
      return Promise.reject(new Error('Ticket ID is required'));
    }
    return api.get(`/admin/tickets/${ticketId}/messages`);
  },

  /**
   * Get ticket statistics overview
   * @returns {Promise<Object>} Ticket statistics
   */
  getStats: () => {
    return api.get('/admin/tickets/stats/overview');
  },
};

export default ticketsApi;
